import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { AbstractControl, NgForm, ValidatorFn, Validators } from "@angular/forms";

import { User } from "src/app/models/user";
import { CHECKOUT_LABEL } from "src/constants/constants";
import {
  EMAIL_PATTERN,
  TEXT_PATTERN,
  MIXED_PATTERN,
  USA_ZIP_PATTERN,
  CANADIAN_POSTAL_PATTERN,
  NUMBER_PATTERN,
  PHONE_PATTERN,
  ADDRESS_PATTERN,
  COUNTRIES
} from "src/constants/validation-patterns";

enum FIELDS {
  FIRST_NAME = <any>'firstName',
  LAST_NAME = <any>'lastName',
  PHONE = <any>'phone',
  EMAIL = <any>'email',
  EMAIL_CONFIRM = <any>'confirmEmail',
  STREET = <any>'street',
  ADDITIONAL_BILLING_ADDRESS_LINE = <any>'additionalBillingAddressLine',
  CITY = <any>'city',
  ZIP_POSTAL_CODE = <any>'zipPostalCode',
  COUNTRY = <any>'country',
  STATE = <any>'state'
}

@Component({
  selector: 'account-form',
  templateUrl: './account-form.html',
  styleUrls: ['./account-form.scss']
})

export class AccountFormComponent {

  @ViewChild('accountForm') accountForm: NgForm;

  @Input() guest: User = {} as User;
  @Input() countriesList: Array<any> = [];
  @Input() statesList: Array<any> = [];
  @Input() submittedAccountForm: boolean = false;
  @Input() emailToConfirm: string = '';
  @Input() euSpecified : boolean = false;
  @Input() braintreeDefaultZipPostalCode: string = '';
  @Input() braintreeDefaultCountry: string = 'United States';
  @Input() braintreeDefaultState: string = '';

  @Output() linkForm = new EventEmitter();
  @Output() validateAddressAndCalculateTaxes = new EventEmitter<string>();
  @Output() rememberAccountInformation = new EventEmitter<void>();
  @Output() readyToCreateLead = new EventEmitter<void>();
  @Output() emitConfirmEmail = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter();
  @Output() stateChange = new EventEmitter();

  readonly FIELDS = FIELDS;
  readonly errorImage: string = 'assets/img/icon-warning-pointer.gif';

  // Validation patterns imported from constants for reusability
  emailPattern: string = EMAIL_PATTERN;
  textPattern: string = TEXT_PATTERN;
  mixedPattern: string = MIXED_PATTERN;
  usaZipPattern: string = USA_ZIP_PATTERN;
  canadianPostalPattern: string = CANADIAN_POSTAL_PATTERN;
  numberPattern: string = NUMBER_PATTERN;
  phonePattern: string = PHONE_PATTERN;
  addressPattern: string = ADDRESS_PATTERN;

  isInvalidState: boolean = false;
  firstValidation: boolean = true;
  hideStateByCountryList: Array<string> = ['United Kingdom', 'Virgin Islands, British'];

  constructor() {}

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupFormValidators();
      this.linkForm.emit(this.accountForm);
    }, 0);
  }

  /**
   * Sets up validators for all form fields
   * Centralizes validator configuration to reduce duplication
   */
  private setupFormValidators(): void {
    if (!this.accountForm?.controls) {
      return;
    }

    // FIRST_NAME and LAST_NAME - same validators
    const nameValidators = [Validators.required, Validators.pattern(this.textPattern)];
    this.setFieldValidators(this.FIELDS.FIRST_NAME, nameValidators);
    this.setFieldValidators(this.FIELDS.LAST_NAME, nameValidators);

    // PHONE - optional field with pattern validation
    this.setFieldValidators(this.FIELDS.PHONE, [Validators.pattern(this.phonePattern)]);

    // EMAIL and EMAIL_CONFIRM - same validators
    const emailValidators = [
      Validators.required,
      Validators.email,
      Validators.pattern(this.emailPattern),
      this.isValidEmails(this),
      this.isConfirmEmails(this)
    ];
    this.setFieldValidators(this.FIELDS.EMAIL, emailValidators);
    this.setFieldValidators(this.FIELDS.EMAIL_CONFIRM, emailValidators);

    // STREET - required with address pattern
    this.setFieldValidators(this.FIELDS.STREET, [
      Validators.required,
      Validators.pattern(this.addressPattern)
    ]);

    // CITY - required with text pattern
    this.setFieldValidators(this.FIELDS.CITY, [
      Validators.required,
      Validators.pattern(this.textPattern)
    ]);

    // ZIP_POSTAL_CODE - uses custom validator based on country
    this.setFieldValidators(this.FIELDS.ZIP_POSTAL_CODE, [
      Validators.required,
      this.validatePostalCode(this)
    ]);
  }

  /**
   * Helper method to set validators for a form field
   * @param fieldName - The field name from FIELDS enum
   * @param validators - Array of validators to apply
   */
  private setFieldValidators(fieldName: FIELDS | string, validators: ValidatorFn[]): void {
    const control = this.accountForm?.controls[String(fieldName)];
    if (control) {
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  /**
   * Gets the localized label for a form field
   * @param label - The label key to retrieve
   * @returns The localized label string
   */
  getLabel(label: string): string {
    try {
      if (this.braintreeDefaultCountry && 
          typeof CHECKOUT_LABEL[this.braintreeDefaultCountry] === 'object' && 
          typeof CHECKOUT_LABEL[this.braintreeDefaultCountry][label] === 'string') {
        return CHECKOUT_LABEL[this.braintreeDefaultCountry][label];
      }
    } catch (e) {
      console.warn('Error getting label for country:', this.braintreeDefaultCountry, e);
    }
    return CHECKOUT_LABEL[COUNTRIES.UNITED_STATES][label] || label;
  }

  /**
   * Returns the appropriate pattern for postal/zip code based on country
   * Used in HTML template for client-side validation
   */
  getPostalCodePattern(): string {
    const country = this.guest?.country || this.braintreeDefaultCountry || COUNTRIES.UNITED_STATES;
    
    if (country === COUNTRIES.UNITED_STATES) {
      return this.usaZipPattern;
    }
    
    if (country === COUNTRIES.CANADA) {
      return this.canadianPostalPattern;
    }
    
    return this.mixedPattern;
  }

  /**
   * Gets the ZIP/Postal code control if it exists
   * @returns The AbstractControl for ZIP_POSTAL_CODE or null
   */
  private getZipPostalCodeControl(): AbstractControl | null {
    return this.accountForm?.controls?.[this.FIELDS.ZIP_POSTAL_CODE] || null;
  }

  validate_AddressAndCalculateTaxes(address: string) {
    // Revalidate postal code when address/country changes
    // Need to reapply validators when country changes because the validator depends on country
    const zipControl = this.getZipPostalCodeControl();
    if (zipControl) {
      // Reapply validators to ensure country-based validation is current
      zipControl.setValidators([
        Validators.required,
        this.validatePostalCode(this)
      ]);
      zipControl.updateValueAndValidity();
      zipControl.markAsTouched(); // Mark as touched to show errors
    }
    this.validateAddressAndCalculateTaxes.emit(address);
  }

  /**
   * Handles blur event on postal code field to trigger validation
   */
  onPostalCodeBlur(): void {
    const zipControl = this.getZipPostalCodeControl();
    if (zipControl) {
      zipControl.updateValueAndValidity();
      zipControl.markAsTouched();
    }
  }

  remember_AccountInformation() {
    this.rememberAccountInformation.emit();
  }

  ready_ToCreateLead() {
    this.readyToCreateLead.emit();
  }

  emitConfirmEmailToCheckout() {
    this.emitConfirmEmail.emit(this.emailToConfirm);
  }

  /**
   * Validator to ensure email and confirm email fields match
   * Note: This validator synchronizes validation between both email fields
   * @param scope - Component scope reference
   * @returns ValidatorFn that validates email confirmation
   */
  isConfirmEmails(scope: AccountFormComponent): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      // Get the parent form group
      const parent = control.parent;
      if (!parent) {
        return null;
      }
      
      // Get both email fields from the form controls
      const emailField = String(scope.FIELDS.EMAIL);
      const confirmEmailField = String(scope.FIELDS.EMAIL_CONFIRM);
      const controls = parent.controls as {[key: string]: AbstractControl};
      
      const emailControl = controls[emailField];
      const confirmEmailControl = controls[confirmEmailField];
      
      if (!emailControl || !confirmEmailControl) {
        return null;
      }
      
      // Get values from form controls (not from component properties)
      const email = emailControl.value;
      const confirmEmail = confirmEmailControl.value;
      
      // If either field is empty, don't validate yet (let required validator handle it)
      if (!email || !confirmEmail) {
        return null;
      }
      
      // Synchronize validation between email and confirm email fields
      if (scope.firstValidation) {
        scope.firstValidation = false;
        
        if (emailControl === control) {
          confirmEmailControl.updateValueAndValidity();
        } else if (confirmEmailControl === control) {
          emailControl.updateValueAndValidity();
        }
        
        scope.firstValidation = true;
      }
      
      // Validate that emails match (case-insensitive)
      const emailsMatch = email.toLowerCase() === confirmEmail.toLowerCase();
      
      return emailsMatch
        ? null
        : {'invalidConfirmEmails': {value: control.value}};
    };
  }

  /**
   * Validator for email format validation
   * Note: This provides a custom error key for HTML template compatibility
   * The actual pattern validation is handled by Validators.pattern()
   * @param scope - Component scope reference
   * @returns ValidatorFn that validates email format
   */
  isValidEmails(scope: AccountFormComponent): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      
      // Validate email pattern and provide custom error key for HTML template
      // Note: Validators.pattern() is also applied, but this provides the custom error key
      const pattern = scope.emailPattern.replace(/^\^/, '').replace(/\$$/, '');
      const regex = new RegExp('^' + pattern + '$');
      
      const isValid = regex.test(control.value);
      return isValid
        ? null
        : {'invalidValidEmail': {value: control.value}};
    };
  }

  validateEmailFields() {
    // Trigger validation on both email fields to keep them synchronized
    if (this.accountForm && this.accountForm.controls) {
      const emailControl = this.accountForm.controls[this.FIELDS.EMAIL];
      const confirmEmailControl = this.accountForm.controls[this.FIELDS.EMAIL_CONFIRM];

      if (emailControl) {
        emailControl.updateValueAndValidity();
      }
      if (confirmEmailControl) {
        confirmEmailControl.updateValueAndValidity();
      }
    }
  }

  /**
   * Validates postal/zip code based on country
   * - USA: ZIP format (91320 or 91320-1234)
   * - Canada: Postal code format (A1A 1A1)
   * - Other countries: General alphanumeric pattern
   * @param scope - Component scope reference
   * @returns ValidatorFn that validates postal code format
   */
  validatePostalCode(scope: AccountFormComponent): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const country = scope.guest?.country || scope.braintreeDefaultCountry || COUNTRIES.UNITED_STATES;
      const value = String(control.value).trim();

      // Map country to pattern and error key
      const countryPatternMap: {[key: string]: {pattern: string, errorKey: string}} = {
        [COUNTRIES.UNITED_STATES]: {
          pattern: scope.usaZipPattern,
          errorKey: 'invalidZipCode'
        },
        [COUNTRIES.CANADA]: {
          pattern: scope.canadianPostalPattern,
          errorKey: 'invalidPostalCode'
        }
      };

      // Get pattern for country or use default mixed pattern
      const countryConfig = countryPatternMap[country] || {
        pattern: scope.mixedPattern,
        errorKey: 'invalidPostalCode'
      };

      const regex = new RegExp(countryConfig.pattern);
      const errorKey = 'pattern'; // Use standard pattern error for HTML compatibility

      if (!regex.test(value)) {
        return {
          [errorKey]: {value: control.value, country: country},
          [countryConfig.errorKey]: {value: control.value, country: country} // Keep custom error for debugging
        };
      }

      return null;
    };
  }

  /**
   * Checks if the app is running in Facebook's in-app browser
   * @returns true if running in Facebook app, false otherwise
   */
  isFacebookApp(): boolean {
    const win = window as any;
    
    if (win.facebookAutofillDisabled) {
      return false; // FB auto fill disabled (e.g., when user clicks PayPal button)
    }
    
    if (win.facebookAutofillComplete) {
      return false; // FB auto fill already completed
    }
    
    const userAgent = navigator.userAgent || navigator.vendor || win.opera || '';
    return userAgent.indexOf("FBAN") > -1 || userAgent.indexOf("FBAV") > -1;
  }

  // Display the state field if it is not included in the hideStateByCountryList
  displayStateField() {
    return !this.hideStateByCountryList.includes(this.guest.country || '');
  }

  onCountryChange(): void {
    this.countryChange.emit(true);
  }

  onStateChange(): void {
    this.stateChange.emit(true);
  }
  facebookAppStateChangeHandler() {
    console.log('facebookAppStateChangeHandler');
    try {
      let fbcountry:any = (document.querySelector('#FBCountryCode') as any).value;

      if(!fbcountry || fbcountry == '') fbcountry = 'US';
      let FBStates = ((window as any)['countries']).find(  ({ value }:{[key:string]:any}) => value === fbcountry);
      if(!FBStates) return;
      let FBState = FBStates.states;
      (document.querySelector('#facebookstate') as any).value = FBState[(document.querySelector('#FBStateCode') as any).value];

      setTimeout(() => {
        if (this.accountForm.form.controls.additionalBillingAddressLine.value == this.accountForm.form.controls.street.value || this.accountForm.form.controls.additionalBillingAddressLine.value == '') {
          if (document.querySelector('#FBAddressLine2') && (document.querySelector('#FBAddressLine2') as any).value != '') {
            this.accountForm.form.controls.additionalBillingAddressLine.patchValue((document.querySelector('#FBAddressLine2') as any).value);
            this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
            this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
          }
        } 
      }, 300);

      if(this.accountForm.form.controls.street.value == this.accountForm.form.controls.additionalBillingAddressLine.value) {
        this.accountForm.form.controls.additionalBillingAddressLine.patchValue('');
        this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
        this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
      }
      if (FBStates.label != '' && FBStates.label != this.accountForm.form.controls.country.value) {
        this.accountForm.form.controls.country.patchValue(FBStates.label);
        this.accountForm.form.controls.country.markAsDirty();
        this.accountForm.form.controls.country.updateValueAndValidity();
        this.accountForm.form.markAsDirty();
       // this.confirmAddress();
       this.validate_AddressAndCalculateTaxes('billing');
       this.onCountryChange();
      }
      setTimeout(() => {
        if (FBState[(document.querySelector('#FBStateCode') as any).value] != '') {
          this.accountForm.form.controls.state.patchValue(FBState[(document.querySelector('#FBStateCode') as any).value]);
          this.accountForm.form.controls.state.markAsDirty();
          this.accountForm.form.controls.state.updateValueAndValidity();

        }
        this.accountForm.form.markAsDirty();

      
      //  this.confirmAddress();
        this.validate_AddressAndCalculateTaxes('billing');
        this.onStateChange();

      },300)
    } catch (e) {
      console.log('Facebook app state changed', e);
    }
  }

}
